/**
 * @fileoverview Content for EditStudentsModal.
 * Layout is a copy of AssignProjectModal — same paddings, flex, styles.
 * Logic is self-contained via useEditStudentsState.
 */
import { Alert, Avatar, Center, Flex, Loader, ScrollArea, TextInput } from '@mantine/core'
import { IconAlertTriangle, IconCircleMinus, IconCirclePlus, IconSearch } from '@tabler/icons-react'
import { Box, Button, Divider, Group, Stack, Text } from 'components'
import { notifications } from 'context'
import { useArcTranslation } from 'hooks'
import { useMantineTheme, useMediaQuery } from 'hooks'
import { updateProjectStudents } from 'pages/ArcProjectsList/services/arcProjectsApi'
import type { FC } from 'react'
import { useQueryClient } from 'react-query'
/** Copied from AssignProjectModal/styles — independent to avoid coupling */
const COLORS = { primary: 'var(--arc-primary, #20927d)', primaryHover: 'var(--arc-primary-hover, #1b7d6b)' } as const
const SPACING = { modal: 24, modalMobile: 16, section: 20, element: 16, small: 8 } as const
const CARD_STYLES = { backgroundColor: '#f8f9fa', borderRadius: 12 }
const SCROLL_STYLES = { scrollbar: { '&[data-orientation="vertical"]': { width: 6 } }, thumb: { backgroundColor: '#d0d0d0' } }
const SEARCH_INPUT_STYLES = { input: { backgroundColor: 'white' } }
const getButtonStyles = (isMobile: boolean) => ({
  cancel: { root: { height: isMobile ? 44 : 40, px: 20, '&:hover': { backgroundColor: '#f5f5f5' } }, label: { fontWeight: 600 } },
  save: { root: { height: isMobile ? 44 : 40, px: 24, backgroundColor: COLORS.primary, '&:hover': { backgroundColor: COLORS.primaryHover }, '&:disabled': { backgroundColor: '#e0e0e0' } }, label: { fontWeight: 600 } },
})
import type { EditStudent } from './useEditStudentsState'
import { useEditStudentsState } from './useEditStudentsState'

const ICON_SIZE = 20
const AVATAR_SIZE = 32

/** Single student row — same as AssignProjectModal's StudentListItem */
const StudentRow: FC<{ student: EditStudent; action: 'add' | 'remove'; onAction: () => void }> = ({
  student, action, onAction,
}) => (
  <Group position='apart' noWrap py={4} px={4}>
    <Group spacing='sm' noWrap>
      <Avatar
        src={student.avatar || null}
        size={AVATAR_SIZE}
        radius='xl'
        color='gray'
        styles={{
          root: { backgroundColor: '#e9ecef', flexShrink: 0 },
          placeholder: { backgroundColor: '#e9ecef', color: '#495057', fontSize: 11, fontWeight: 600, letterSpacing: '0.3px' },
        }}
      >
        {student.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
      </Avatar>
      <Text size='sm'>{student.name}</Text>
    </Group>
    <Button variant='subtle' compact p={0} onClick={onAction}>
      {action === 'add'
        ? <IconCirclePlus size={ICON_SIZE} color={COLORS.primary} />
        : <IconCircleMinus size={ICON_SIZE} color='#fa5252' />}
    </Button>
  </Group>
)

interface EditStudentsContentProps {
  projectId: string
  schoolTermId: string
  groupName: string
  startDate?: string | null
  onClose: () => void
  onRequestClose: () => void
  openConfirm: () => void
}

export const EditStudentsContent: FC<EditStudentsContentProps> = ({
  projectId, schoolTermId, groupName, startDate, onClose, onRequestClose, openConfirm,
}) => {
  const { t } = useArcTranslation()
  const queryClient = useQueryClient()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`) ?? false
  const modalPadding = isMobile ? SPACING.modalMobile : SPACING.modal
  const buttonStyles = getButtonStyles(isMobile)

  const {
    assignedStudents, availableStudents,
    isLoading, isSaving, setIsSaving, isDirty,
    searchQuery, setSearchQuery,
    addStudent, removeStudent, addAllStudents,
    assignedIds,
  } = useEditStudentsState({ projectId, schoolTermId, opened: true })

  async function handleSave() {
    setIsSaving(true)
    try {
      const result = await updateProjectStudents(projectId, Array.from(assignedIds))
      if (result.success) {
        notifications.show({ message: t('edit_students.saved_tracker_updating'), color: 'green' })
        queryClient.invalidateQueries(['arc-projects'])
        queryClient.invalidateQueries(['arc-project-participants', projectId])
        onClose()
      } else {
        notifications.show({ message: result.error || t('edit_students.error'), color: 'red' })
      }
    } catch {
      notifications.show({ message: t('edit_students.error'), color: 'red' })
    } finally {
      setIsSaving(false)
    }
  }

  function handleRequestClose() {
    isDirty ? openConfirm() : onRequestClose()
  }

  if (isLoading) {
    return (
      <Flex direction='column' sx={{ flex: 1, overflow: 'hidden' }} mih={0}>
        <Box px={modalPadding} py={SPACING.section}>
          <Group grow spacing={SPACING.element} noWrap>
            <Box sx={{ height: 68, borderRadius: 8, backgroundColor: '#f5f5f5' }} />
          </Group>
        </Box>
        <Center sx={{ flex: 1 }}><Loader size='sm' /></Center>
      </Flex>
    )
  }

  return (
    <Flex direction='column' sx={{ flex: 1, overflow: 'hidden' }} mih={0}>
      <Flex
        direction='column'
        px={modalPadding}
        py={SPACING.section}
        sx={{
          flex: 1, overflow: 'hidden',
          opacity: isSaving ? 0.5 : 1,
          pointerEvents: isSaving ? 'none' : 'auto',
        }}
        mih={0}
      >
        <Stack spacing={SPACING.section} sx={{ flex: 1, overflow: 'hidden' }} mih={0}>
          {/* Read-only project class and start date */}
          <Group spacing='xl' noWrap>
            <Box>
              <Text size='sm' fw={600}>{t('edit_students.project_class')}</Text>
              <Text size='sm' color='dimmed'>{groupName || '—'}</Text>
            </Box>
            {startDate && (
              <Box>
                <Text size='sm' fw={600}>{t('edit_students.start_date')}</Text>
                <Text size='sm' color='dimmed'>{startDate}</Text>
              </Box>
            )}
          </Group>

          {/* Warning banner */}
          <Alert icon={<IconAlertTriangle size={18} />} color='yellow' radius='md'>
            {t('edit_students.warning')}
          </Alert>

          {/* Two-panel layout — same as StudentsPanel in assign modal */}
          <Group spacing={SPACING.element} noWrap sx={{ flex: 1, overflow: 'hidden' }} align='stretch'>
            {/* Left: Available students */}
            <Flex direction='column' sx={{ flex: 1, overflow: 'hidden' }}>
              <Group position='apart' mb={SPACING.small}>
                <Text fw={600} size='sm'>{t('assign_project.your_students')}</Text>
                <Button variant='subtle' compact size='xs' color='teal' onClick={addAllStudents}>
                  + {t('assign_project.add_all_students')}
                </Button>
              </Group>
              <TextInput
                size='sm'
                icon={<IconSearch size={14} />}
                placeholder={t('assign_project.search_by_name')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.currentTarget.value)}
                mb={SPACING.small}
                styles={SEARCH_INPUT_STYLES}
              />
              <ScrollArea sx={{ flex: 1 }} styles={SCROLL_STYLES}>
                {availableStudents.map(s => (
                  <StudentRow key={s.id} student={s} action='add' onAction={() => addStudent(s.id)} />
                ))}
                {availableStudents.length === 0 && (
                  <Center py='xl'>
                    <Text color='dimmed' size='sm' align='center'>
                      {t('assign_project.all_students_assigned')}
                    </Text>
                  </Center>
                )}
              </ScrollArea>
            </Flex>

            {/* Right: Assigned students */}
            <Flex
              direction='column'
              sx={{ flex: 1, overflow: 'hidden', ...CARD_STYLES, padding: SPACING.element }}
            >
              <Text fw={600} size='sm' mb={SPACING.small}>{t('assign_project.assigned_students')}</Text>
              <ScrollArea sx={{ flex: 1 }} styles={SCROLL_STYLES}>
                {assignedStudents.map(s => (
                  <StudentRow key={s.id} student={s} action='remove' onAction={() => removeStudent(s.id)} />
                ))}
                {assignedStudents.length === 0 && (
                  <Center sx={{ flex: 1 }}>
                    <Text color='dimmed' size='sm' align='center'>
                      {t('assign_project.arc_empty_students_message')}
                    </Text>
                  </Center>
                )}
              </ScrollArea>
            </Flex>
          </Group>
        </Stack>
      </Flex>

      {/* Footer — same as ModalFooter in assign modal */}
      <Group
        position={isMobile ? 'apart' : 'right'}
        px={modalPadding}
        py={SPACING.element}
        grow={isMobile}
        spacing={SPACING.small}
      >
        <Button variant='default' onClick={handleRequestClose} radius='xl' size='md' fullWidth={isMobile} styles={buttonStyles.cancel}>
          {t('general_buttons_cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          loading={isSaving}
          radius='xl'
          size='md'
          fullWidth={isMobile}
          styles={buttonStyles.save}
        >
          {t('edit_students.save_changes')}
        </Button>
      </Group>
    </Flex>
  )
}
